import axios from 'axios';
import {
  Employee,
  CreateEmployeeRequest,
  UpdateEmployeeRequest,
  enumToDesignation
} from '@/types/employees';
import { API_BASE_URL, createAuthenticatedApi, handleUnauthorized } from '@/lib/api';

const api = createAuthenticatedApi(API_BASE_URL);

// Get all employees
export const getEmployees = async (): Promise<Employee[]> => {
  try {
    const response = await api.get('/employees');
    // Map the response to match the Employee interface
    return response.data.data.map((employee: any): Employee => ({
      ...employee,
      branchName: employee.branch?.branchName || '',
      designation: enumToDesignation[employee.designation as keyof typeof enumToDesignation] || employee.designation,
      assignedBranchIds: employee.assignedBranchIds || [employee.branchId], // Use branchId if assignedBranchIds is null
      passportExpiry: employee.passportExpiry ? new Date(employee.passportExpiry).toISOString().split('T')[0] : '',
      visaExpiry: employee.visaExpiry ? new Date(employee.visaExpiry).toISOString().split('T')[0] : '',
      dateOfBirth: employee.dateOfBirth ? new Date(employee.dateOfBirth).toISOString().split('T')[0] : ''
    }));
  } catch (error) {
    console.error('Error fetching employees:', error);
    throw error;
  }
};

// Get single employee by ID
export const getEmployeeById = async (id: string): Promise<Employee> => {
  try {
    const response = await api.get(`/employees/${id}`);
    const employee = response.data.data;
    // Map the response to match the Employee interface
    return {
      ...employee,
      branchName: employee.branch?.branchName || '',
      designation: enumToDesignation[employee.designation as keyof typeof enumToDesignation] || employee.designation,
      assignedBranchIds: employee.assignedBranchIds || [employee.branchId], // Use branchId if assignedBranchIds is null
      passportExpiry: employee.passportExpiry ? new Date(employee.passportExpiry).toISOString().split('T')[0] : '',
      visaExpiry: employee.visaExpiry ? new Date(employee.visaExpiry).toISOString().split('T')[0] : '',
      dateOfBirth: employee.dateOfBirth ? new Date(employee.dateOfBirth).toISOString().split('T')[0] : ''
    };
  } catch (error) {
    console.error('Error fetching employee:', error);
    throw error;
  }
};

// Create new employee
export const createEmployee = async (employeeData: CreateEmployeeRequest): Promise<Employee> => {
  try {
    const response = await api.post('/employees', employeeData);
    return response.data.data;
  } catch (error) {
    console.error('Error creating employee:', error);
    throw error;
  }
};

// Update employee
export const updateEmployee = async (id: string, employeeData: UpdateEmployeeRequest): Promise<Employee> => {
  try {
    const response = await api.put(`/employees/${id}`, employeeData);
    return response.data.data;
  } catch (error) {
    console.error('Error updating employee:', error);
    throw error;
  }
};

// Delete employee (soft delete)
export const deleteEmployee = async (id: string): Promise<void> => {
  try {
    await api.delete(`/employees/${id}`);
  } catch (error) {
    console.error('Error deleting employee:', error);
    throw error;
  }
};

// Update employee status
export const updateEmployeeStatus = async (
  id: string, 
  status: 'ACTIVE' | 'INACTIVE' | 'ON_LEAVE' | 'TERMINATED',
  reason?: string
): Promise<Employee> => {
  try {
    const response = await api.patch(`/employees/${id}/status`, { status, reason });
    return response.data.data;
  } catch (error) {
    console.error('Error updating employee status:', error);
    throw error;
  }
};

// Bulk update employee status
export const bulkUpdateEmployeeStatus = async (
  employeeIds: string[],
  status: 'ACTIVE' | 'INACTIVE' | 'ON_LEAVE' | 'TERMINATED',
  reason?: string
): Promise<void> => {
  try {
    await api.post('/employees/bulk-status', { employeeIds, status, reason });
  } catch (error) {
    console.error('Error bulk updating employee status:', error);
    throw error;
  }
};

// Get document expiry alerts
export const getDocumentExpiryAlerts = async (): Promise<any[]> => {
  try {
    const response = await api.get('/employees/expiry-alerts');
    return response.data.data;
  } catch (error) {
    console.error('Error fetching document expiry alerts:', error);
    throw error;
  }
};
